<template>
  <v-dialog v-model="visible" width="800" persistent>
    <div class="download-dialog-content" v-loading="loading" element-loading-text="正在下载数据，请稍候..."
      element-loading-background="rgba(255, 255, 255, 0.8)">
      <!-- 标题栏 -->
      <div class="dialog-header">
        <span class="dialog-title">数据下载</span>
        <v-icon icon="mdi-close" class="close-icon" @click="handleClose" color="#000000"></v-icon>
      </div>

      <!-- 内容区域 -->
      <div class="dialog-body">
        <!-- 设备类型 -->
        <div class="form-section">
          <div class="section-label">设备类型：</div>
          <div class="device-types">
            <div class="type-btn" :class="{ active: form.deviceType === 'link' }"
              @click="form.deviceType = 'link'; onDeviceTypeChange()">能量链</div>
            <div class="type-btn" :class="{ active: form.deviceType === 'block' }"
              @click="form.deviceType = 'block'; onDeviceTypeChange()">能量块</div>
            <div class="type-btn" :class="{ active: form.deviceType === 'meter' }"
              @click="form.deviceType = 'meter'; onDeviceTypeChange()">电表</div>
          </div>
        </div>

        <!-- 查询范围 -->
        <div class="form-section date-section">
          <div class="section-label">查询范围：</div>
          <div class="date-range">
            <el-date-picker v-model="form.dateRange" type="datetimerange" range-separator="~" start-placeholder="开始时间"
              end-placeholder="结束时间" format="YYYY-MM-DD HH:mm:ss" value-format="YYYY-MM-DD HH:mm:ss" class="date-picker"
              :teleported="false" prefix-icon="none" :clearable="false" :default-time="defaultTime" />
          </div>
        </div>

        <!-- 设备选择 -->
        <div class="form-section small-section">
          <div class="section-label">设备选择:</div>
          <div class="device-select">
            <!-- 电表选择 -->
            <el-select v-if="form.deviceType === 'meter'" v-model="form.selectedDevice" placeholder="请选择电表"
              class="device-selector" :teleported="false">
              <el-option v-for="item in deviceLists.meters" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>

            <!-- 能量链选择 -->
            <el-select v-else-if="form.deviceType === 'link'" v-model="form.selectedDevice" placeholder="请选择能量链"
              class="device-selector" :teleported="false">
              <el-option v-for="item in deviceLists.eLinks" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>

            <!-- 能量块选择：先选能量链，再选能量块 -->
            <template v-else-if="form.deviceType === 'block'">
              <el-select v-model="form.selectedELink" placeholder="请先选择能量链" class="device-selector elink-selector"
                :teleported="false" @change="onELinkChange">
                <el-option v-for="item in deviceLists.eLinks" :key="item.value" :label="item.label"
                  :value="item.value" />
              </el-select>
              <el-select v-model="form.selectedDevice" placeholder="请选择能量块" class="device-selector eblock-selector"
                :teleported="false" :disabled="!form.selectedELink">
                <el-option v-for="item in deviceLists.eBlocks" :key="item.value" :label="item.label"
                  :value="item.value" />
              </el-select>
            </template>
          </div>
        </div>
      </div>

      <!-- 底部按钮 -->
      <div class="dialog-footer">
        <div class="footer-btns">
          <button class="cancel-btn" @click="handleClose">取消</button>
          <button class="confirm-btn" @click="handleDownload">开始下载</button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'
import request from '@/utils/request'
import { saveAs } from 'file-saver'
import { blobValidate } from '@/utils/ruoyi'
import { eui } from 'ems-ui'
import { list as getMeterList } from '@/api/device/meter'
import { getElinkList, exportStationData } from '@/api/station'

const defaultTime = [
  new Date(2000, 1, 1, 0, 0, 0),
  new Date(2000, 1, 1, 23, 59, 59)
]

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  isOldStation: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

// 从 URL 获取 stationId
const route = useRoute()
const stationId = computed(() => route.query.stationId || route.params.stationId || '')

// Loading 状态
const loading = ref(false)

// 控制显示隐藏
const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

// 表单数据
const form = reactive({
  deviceType: 'link',
  selectedDevice: '',
  selectedELink: '', // 选择能量块时，先选择的能量链
  dateRange: [
    dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
  ]
})

// 设备列表
const deviceLists = reactive({
  meters: [],
  eLinks: [],
  eBlocks: []
})

// 当前能量链列表（用于获取能量块）
const eLinkList = ref([])

// 设备类型选项
const deviceTypeOptions = [
  { label: '能量链', value: 'link', tableName: 'elink_info' },
  { label: '能量块', value: 'block', tableName: 'eblock_info' },
  { label: '电表', value: 'meter', tableName: 'meter_info' }
]

// 获取电表列表
const fetchMeterList = async (stationId) => {
  try {
    const res = await getMeterList(1, 65535, stationId)
    if (res.rows && res.rows.length > 0) {
      deviceLists.meters = res.rows.map(item => ({
        label: item.meterName,
        value: item.uuid
      }))
      // 默认选中第一个
      if (form.deviceType === 'meter') {
        form.selectedDevice = deviceLists.meters[0].value
      }
    }
  } catch (error) {
    console.error('获取电表列表失败:', error)
  }
}

// 获取能量链和能量块列表
const fetchELinkAndEBlock = async (stationId) => {
  try {
    const res = await getElinkList(stationId)
    if (res.data && res.data.length > 0) {
      eLinkList.value = res.data
      deviceLists.eLinks = res.data.map(item => ({
        label: item.equipmentName,
        value: item.uuid
      }))
      
      // 根据当前设备类型设置默认选中
      if (form.deviceType === 'link') {
        form.selectedDevice = deviceLists.eLinks[0].value
      } else if (form.deviceType === 'block') {
        // 能量块类型：先选择能量链
        form.selectedELink = deviceLists.eLinks[0].value
        updateEBlockList(form.selectedELink)
      } else {
        // 预加载第一个能量链的能量块列表
        updateEBlockList(res.data[0].uuid)
      }
    }
  } catch (error) {
    console.error('获取能量链列表失败:', error)
  }
}

// 更新能量块列表
const updateEBlockList = (eLinkUuid) => {
  const eLink = eLinkList.value.find(item => item.uuid === eLinkUuid)
  if (eLink && eLink.list) {
    deviceLists.eBlocks = eLink.list.map(item => ({
      label: item.equipmentName,
      value: item.uuid,
      productModel: item.productModel
    }))
    // 默认选中第一个能量块
    if (form.deviceType === 'block' && deviceLists.eBlocks.length > 0) {
      form.selectedDevice = deviceLists.eBlocks[0].value
    }
  }
}

// 能量链选择改变（用于能量块级联选择）
const onELinkChange = (eLinkUuid) => {
  // 清空之前选择的能量块
  form.selectedDevice = ''
  // 更新能量块列表
  updateEBlockList(eLinkUuid)
}

// 设备类型改变
const onDeviceTypeChange = () => {
  form.selectedDevice = ''
  form.selectedELink = ''

  if (form.deviceType === 'meter') {
    if (deviceLists.meters.length > 0) {
      form.selectedDevice = deviceLists.meters[0].value
    } else {
      fetchMeterList(stationId.value)
    }
  } else if (form.deviceType === 'link') {
    if (deviceLists.eLinks.length > 0) {
      form.selectedDevice = deviceLists.eLinks[0].value
    }
  } else if (form.deviceType === 'block') {
    // 能量块需要先选择能量链
    if (deviceLists.eLinks.length > 0) {
      form.selectedELink = deviceLists.eLinks[0].value
      updateEBlockList(form.selectedELink)
    }
  }
}

// 获取表名
const getTableName = () => {
  const typeOption = deviceTypeOptions.find(item => item.value === form.deviceType)
  let tableName = typeOption?.tableName || ''
  
  // 特殊处理表名
  if (form.deviceType === 'meter') {
    tableName = 'meter'
  }
  
  return tableName
}

// 关闭弹窗
const handleClose = () => {
  visible.value = false
}

// 下载数据
const handleDownload = async () => {
  if (!form.selectedDevice) {
    console.warn('请选择设备')
    eui.showToast('请选择设备', 'warning')
    return
  }

  if (!form.dateRange || form.dateRange.length !== 2) {
    console.warn('请选择时间范围')
    eui.showToast('请选择时间范围', 'warning')
    return
  }

  const [startTime, endTime] = form.dateRange

  // 检查时间范围（TDengine限制一天内）
  const timeDiff = dayjs(endTime).diff(dayjs(startTime))
  if (timeDiff > 86400000) {
    console.warn('时间范围过大，请选择一天之内的数据')
    eui.showToast('时间范围过大，请选择一天之内的数据', 'warning')
    return
  }

  const realTableName = getTableName()
  // 获取模型名称（使用配置中的原始表名）
  const typeOption = deviceTypeOptions.find(item => item.value === form.deviceType)
  const modelName = typeOption?.tableName || realTableName

  // 根据设备类型确定下载字段
  let columns = 'record_time,receive_time'
  if (form.deviceType === 'meter') {
    columns = 'record_time,receive_time,u_ab,u_bc,u_ca,i_a,i_b,i_c,p_a,p_b,p_c,p_total'
  } else if (form.deviceType === 'block') {
    columns = 'record_time,receive_time,u_ab,u_bc,u_ca,i_a,i_b,i_c'
  } else if (form.deviceType === 'link') {
    columns = 'record_time,receive_time,p_total,q_total,pf'
  }

  const params = {
    tableName: realTableName,
    modelName: modelName,
    selectDataColumnNames: columns,
    startTime: startTime,
    endTime: endTime,
    stationId: Number(stationId.value),
    uuid: form.selectedDevice,
    isOldStation: props.isOldStation ? '1' : '0',
    // engine: 'TDengine'
  }

  const filename = `${form.selectedDevice}_${realTableName}_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`

  try {
    loading.value = true
    const res = await exportStationData(params)

    const isBlob = await blobValidate(res)
    if (isBlob) {
      const blob = new Blob([res])
      saveAs(blob, filename)
      eui.showToast('下载成功', 'success')
      visible.value = false
    } else {
      const resText = await res.text()
      const rspObj = JSON.parse(resText)
      eui.showToast(rspObj.msg || '下载失败', 'error')
    }
  } catch (error) {
    console.error('下载失败:', error)
    eui.showToast('下载失败', 'error')
  } finally {
    loading.value = false
  }
}

// 监听弹窗显示，初始化数据
watch(visible, (newVal) => {
  if (newVal) {
    // 重置日期范围
    form.dateRange = [
      dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
    ]

    // 获取设备列表
    if (deviceLists.eLinks.length === 0) {
      fetchELinkAndEBlock(stationId.value)
    } else {
      // 如果已有列表，设置默认选中
      if (form.deviceType === 'link' && deviceLists.eLinks.length > 0) {
        form.selectedDevice = deviceLists.eLinks[0].value
      } else if (form.deviceType === 'block' && deviceLists.eLinks.length > 0) {
        // 能量块类型：先选择能量链
        form.selectedELink = deviceLists.eLinks[0].value
        updateEBlockList(form.selectedELink)
      }
    }
  }
})
</script>

<style scoped lang="scss">
.download-dialog-content {
  width: 800px;
  height: 544px;
  background: linear-gradient(180deg, rgba(248, 252, 255, 1) 0%, rgba(249, 250, 252, 1) 101.29%);
  border-radius: 30px;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: visible;

  .dialog-header {
    height: 23px;
    margin-top: 28px;
    padding: 0 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .dialog-title {
      font-family: 'HarmonyOS Sans SC', sans-serif;
      font-size: 20px;
      color: rgba(0, 0, 0, 1);
      font-weight: 400;
    }

    .close-icon {
      cursor: pointer;
      font-size: 16px;
    }
  }

  .dialog-body {
    padding: 0 28px;
    margin-top: 28px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .form-section {
      width: 744px;
      min-height: 123px;
      background: rgba(255, 255, 255, 1);
      border-radius: 20px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      &.date-section {
        min-height: 100px;
        height: auto;
        padding-bottom: 20px;
      }

      &.small-section {
        min-height: 60px;
        height: auto;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: 20px 20px 20px 0;

        .section-label {
          margin-top: 0;
        }
      }

      .section-label {
        margin-top: 20px;
        margin-left: 20px;
        font-family: 'HarmonyOS Sans SC', sans-serif;
        font-size: 16px;
        color: rgba(46, 46, 46, 1);
        font-weight: 400;
      }

      .device-types {
        margin-top: 16px;
        margin-left: 20px;
        width: 704px;
        height: 48px;
        display: flex;
        gap: 10px;

        .type-btn {
          width: 228px;
          height: 48px;
          border-radius: 10px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-family: 'HarmonyOS Sans SC', sans-serif;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s;
          border: 1px solid rgba(224, 228, 231, 1);
          color: rgba(46, 46, 46, 1);
          background: transparent;

          &.active {
            background: rgba(58, 104, 255, 0.08) !important;
            color: rgba(58, 104, 255, 1) !important;
            border: 1px solid rgba(58, 104, 255, 1) !important;
            font-weight: 500;
          }
        }
      }

      .date-range {
        margin-top: 16px;
        margin-left: 20px;
        width: 704px;
        display: flex;
        justify-content: center;
        align-items: center;


        /* 针对 Element Plus DatePicker Range 类型 */
        :deep(.el-date-editor) {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0;
          height: 50px !important;
          border: none !important;

          .el-range-input {
            background: rgba(247, 248, 250, 1);
            border-radius: 8px;
            height: 50px;
            line-height: 40px;
            color: rgba(46, 46, 46, 1);
            font-family: 'HarmonyOS Sans SC', sans-serif;
            font-size: 16px;
            flex: 3;
            padding: 0 15px;
            text-align: center;

            &::placeholder {
              color: rgba(46, 46, 46, 0.4);
            }
          }

          :deep(.el-range-separator) {
            width: 30px;
            color: rgba(46, 46, 46, 1);
            font-family: 'HarmonyOS Sans SC', sans-serif;
            padding: 0;
            line-height: 40px;
          }

          /* 隐藏图标 */
          :deep(.el-input__icon),
          :deep(.el-range__icon),
          :deep(.el-range__close-icon) {
            display: none;
          }

          /* 消除悬停和聚焦时的外层样式 */
          &.is-active,
          &:hover {
            box-shadow: none !important;
            background: transparent !important;
          }
        }
      }

      .device-select {
        flex: 1;
        margin-left: 20px;
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        

        .device-selector {
          // flex: 1;
          width: 40% !important;

          &.elink-selector,
          &.eblock-selector {
            flex: 1;
            min-width: 180px;
          }

          :deep(.el-select__wrapper),
          :deep(.el-input__wrapper) {
            background: rgba(247, 248, 250, 1) !important;
            border-radius: 8px;
            box-shadow: none !important;
            padding: 4px 15px;
            height: 50px;
            transition: all 0.3s ease;
            font-size: 16px !important;

            .el-select__selected-item {
              color: #2E2E2E !important;
            }

            &:hover {
              background: rgba(247, 248, 250, 1) !important;
            }

            &.is-focused,
            &.is-focus {
              box-shadow: none !important;
            }
          }
        }
      }
    }
  }

  .dialog-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 800px;
    height: 96px;
    background: #fff;
    border-radius: 0 0 30px 30px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding-top: 20px;
    padding-right: 28px;

    .footer-btns {
      display: flex;
      gap: 20px;

      button {
        height: 48px;
        border-radius: 10px;
        font-family: 'HarmonyOS Sans SC', sans-serif;
        font-size: 16px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .cancel-btn {
        width: 120px;
        border: 1px solid rgba(224, 228, 231, 1);
        color: rgba(46, 46, 46, 1);
        background: transparent;
      }

      .confirm-btn {
        width: 180px;
        background: rgba(58, 104, 255, 1);
        color: rgba(255, 255, 255, 1);
        font-weight: 500;
        border: none;
      }
    }
  }
}
</style>
